/**
 * @file 群策引擎
 * @author gaoxunchen
 */

import _ from 'lodash';
import React, { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Button, ButtonGroup, Loading } from '@tencent/spaui';
import { Flex } from '@tencent/odc';
import Icon from '@tencent/od-icon';
import dayjs from 'dayjs';

import globalStore from '@/stores';
import { hasPurview, hasFeatureFlag } from '@/services';
import { currencyFormatV2 } from '@/pages/agency/utils/format';

import HomeItem from '../home-item';
import Summary, { SummaryTheme } from '../summary';
import { getActiveSummary, getPastSummary, getStrategySummary } from './actions';
import CardInternalExternalEvent from '@/pages/agency/task/internal-external-event/home-entry-card';

import './index.scoped.less';

const date = dayjs();
const startDate = date
  .add(-1, 'day')
  .startOf('quarter')
  .format('YYYY-MM-DD');
const endDate = date
  .add(-1, 'day')
  .format('YYYY-MM-DD');

const ACTION_MAP: Record<string, any> = {
  active: {
    action: getActiveSummary,
    params: {
      begin_date_begin: startDate,
      begin_date_end: endDate,
    },
  },
  past: {
    action: getPastSummary,
    params: {
      begin_date_begin: startDate,
      begin_date_end: endDate,
      end_date_begin: startDate,
      end_date_end: endDate,
    },
  },
  strategy: {
    action: getStrategySummary,
    params: {
      begin_date_begin: startDate,
      begin_date_end: endDate,
    },
  },
};

const CardTask: React.FC<any> = observer((props) => {
  const { title, schema = {} } = props;
  const { account, history } = globalStore;
  const showHeaderBanner = _.get(schema, 'showHeaderBanner', false);
  const _typeList = _.get(schema, 'typeList', []);
  const [type, setType] = useState(_.get(_typeList, '0.value', 'personal'));
  const typeData = _.find(_typeList, (v) => v.value === type) || {};
  const typeList = _typeList.filter((v: any) => {
    const { value, hide = false } = v;
    if (hide) {
      return false;
    }
    switch (value) {
      case 'team':
        return account.dynamicNavAuth?.taskTeamEnable;
      case 'strategy':
        return hasPurview(100000000) && hasFeatureFlag('is_private_strategy_enable');
    }
    return true;
  });

  const [personalData, setPersonalData] = useState<Record<string, any>>({});
  const [teamData, setTeamData] = useState<Record<string, any>>({});
  const [strategyData, setStrategyData] = useState<Record<string, any>>({});

  const data: any = {
    personal: personalData,
    team: teamData,
    strategy: strategyData,
  };

  const setDataMap: any = {
    personal: setPersonalData,
    team: setTeamData,
    strategy: setStrategyData,
  };

  useEffect(() => {
    const _data = data[type] || {};
    const setData = setDataMap[type];
    if (!_.isEmpty(_data) || !_.isFunction(setData)) {
      return;
    }
    (async () => {
      const actionList = _.get(typeData, 'actionList', []);
      const resList = await Promise.allSettled((
        actionList.map((v: any) => {
          const params = {
            ...(ACTION_MAP[v.action]?.params || {}),
            ...(v.params || {}),
          };
          return getData(ACTION_MAP[v.action]?.action || '', params, v.action === 'strategy' ? 'data' : 'data.list.0');
        })
      ));
      const data = {};
      actionList.forEach((v: any, i: number) => {
        _.set(data, v.key, _.get(resList, `${i}.value`, {}));
      });
      setData(data);
    })();
  }, [
    type,
  ]);

  const getData = async (action: any, params: Record<string, any>, dataPath = 'data') => {
    try {
      const res = await action(params);
      const data = _.get(res, dataPath, {});
      return data;
    } catch (error) {
      return {};
    }
  };

  const getValue = (v: Record<string, any>) => {
    if (_.isEmpty(data[type])) {
      return <Loading />;
    }
    let value = _.get(data, `${type}.${v.key}`, null);
    if (_.isNil(value)) {
      return '-';
    }
    switch (v.key) {
      case 'public.past.task_done_rate':
        value = _.isFinite(value) ? `${value.toFixed(0)}%` : '-';
        break;
      default:
        value = currencyFormatV2(value);
        break;
    }
    return (
      <span style={v.style || {}}>
        {value}
      </span>
    );
  };

  if (account.onlyTechAgency) {
    return null;
  }

  const isEnableAgencyEvent = hasPurview(138) && hasFeatureFlag('is_enable_agency_event');

  const CardTaskComponent = useMemo(() => (
    <div className={`card-task${isEnableAgencyEvent ? ' card-task-compact' : ''}`}>
      {isEnableAgencyEvent ? (
        <>
          <div className="card-task-header">
            <div className="left">
              <span className="card-task-title">策略</span>
            </div>
            <div className="right">
              <span>{`所示数据的时间范围：${startDate} 至 ${endDate}`}</span>
              <Button
                className="more"
                displayType="link"
                endIcon={<Icon name="right-outlined" />}
                data-report="home-jump-task-more"
                onClick={() => history.push(_.get(typeData, 'more', ''))}
              >
                更多
              </Button>
            </div>
          </div>
          <div className="card-task-toolbar">
            <ButtonGroup
              theme='light'
              data={typeList}
              value={type}
              onChange={(_event, value) => setType(value)}
            />
          </div>
        </>
      ) : (
        <div className="card-task-header">
          <div className="left">
            <ButtonGroup
              theme='light'
              data={typeList}
              value={type}
              onChange={(_event, value) => setType(value)}
            />
          </div>
          <div className="right">
            <span>{`所示数据的时间范围：${startDate} 至 ${endDate}`}</span>
            <Button
              className="more"
              displayType="link"
              endIcon={<Icon name="right-outlined" />}
              data-report="home-jump-task-more"
              onClick={() => history.push(_.get(typeData, 'more', ''))}
            >
              更多
            </Button>
          </div>
        </div>
      )}
      <Summary
        theme={SummaryTheme.theme2}
        schema={_.get(schema, type, [])}
        getValue={getValue}
      />
    </div>
  ), [type, data, typeList, schema, startDate, endDate, typeData, history, isEnableAgencyEvent]);

  // 自定义 header（蓝色横幅）配置
  const customHeaderProps = showHeaderBanner ? {
    className: 'with-custom-header',
    header: (
      <div className="home-item-header-inner">
        <div className="header-bg"></div>
        <div className="header-right"></div>
        <div className="header-content">
          <span className="title">{title}</span>
          <span className="icon-new"></span>
          <span className="slogan"></span>
        </div>
      </div>
    ),
  } : {
    title,
  };

  return (
    <HomeItem {...customHeaderProps}>
      {isEnableAgencyEvent ? <Flex gap={24} className="card-task-external-wrapper">
        <div className="card-task-external-item">
          <CardInternalExternalEvent />
        </div>
        <div className="card-task-external-item card-task-strategy-item">
          {CardTaskComponent}
        </div>
      </Flex> : CardTaskComponent}
    </HomeItem>
  );
});

export default CardTask;
